/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import flow from 'lodash.flow';
import { getLogger } from 'src/core/services/telemetry/telemetryLogger';
import { HandlerGroupFactoryDeps, HandlerParams } from 'src/core/types/handler';
import { addDirtyState } from 'src/core/utils/decorators/addDirtyState';
import { addInvalidatePredictions } from 'src/core/utils/decorators/addInvalidatePredictions';
import { getPrebuiltEntityService } from 'src/luis/core/services/entities/prebuiltEntityService';
import { loadEntitiesHelper } from 'src/luis/projects/entities/utils/loadCommonEntitiesHelper';
import { LuisId } from 'src/luis/types/Resource';
import { DomainModel } from 'src/luis/types/stores/domains/DomainStore';
import { EntityStore } from 'src/luis/types/stores/entities/EntityStore';
import { PrebuiltEntityStore } from 'src/luis/types/stores/entities/PrebuiltEntityMetadataStore';

export type PrebuiltEntityHandlerGroup = ReturnType<typeof createPrebuiltEntityHandlerGroup>;

/**
 * Represents a handler group for entity role operations.
 *
 * @param dependencies The handler group dependencies.
 */
export const createPrebuiltEntityHandlerGroup = (dependencies: HandlerGroupFactoryDeps) => {
    const { rootStore } = dependencies;
    const logger = getLogger();

    /**
     * load the prebuilt entities.
     *
     * @param param0.appId The id of the application.
     * @param param0.versionId The id of the version.
     */
    const loadPrebuiltEntities = async (params?: HandlerParams) => {
        const { context } = params;
        const { prebuiltEntityStoreHouse } = rootStore.luisAppStoreHouse.findById(context.appId);
        const prebuiltEntityService = getPrebuiltEntityService({ context });

        if (!context.forceRefresh && (prebuiltEntityStoreHouse.isLoading || prebuiltEntityStoreHouse.isLoaded)) {
            return;
        }
        const logTimedActivity = logger.createTimedActivityLogger<PrebuiltEntityStore[]>('loadPrebuiltEntities');
        prebuiltEntityStoreHouse.setIsLoading(true);
        let prebuiltEntities = await logTimedActivity(prebuiltEntityService.getPrebuiltEntities());

        const isDatetimeV2 = prebuiltEntities.find(pE => pE.name === 'datetimeV2');
        // Removes date time v1 if v2 existed else we display v1
        if (isDatetimeV2) {
            prebuiltEntities = prebuiltEntities.filter(item => item.name !== 'datetime');
        }

        prebuiltEntityStoreHouse.setItems(prebuiltEntities.filter(item => item.name !== 'encyclopedia' && item.name !== 'geography'));

        prebuiltEntityStoreHouse.setIsLoading(false);
        prebuiltEntityStoreHouse.setIsLoaded(true);
    };

    /**
     * Add a prebuilt entities to the current app.
     *
     * @param param0.entityNames The names of prebuilt entities to be added.
     */
    const addPrebuiltEntities = async ({ context, entityNames }: HandlerParams<{ entityNames: string[] }>) =>
        flow(async () => {
            const prebuiltEntityService = getPrebuiltEntityService({ context });
            const logTimedActivity = logger.createTimedActivityLogger<EntityStore[]>('AddPrebuiltEntities', () => ({ entityNames }));

            const entities = await logTimedActivity(prebuiltEntityService.addPrebuiltEntities(entityNames));

            rootStore.currentLuisAppStore.currentAppVersionStore.entityStoreHouse.batchAdd(entities);
        }, addInvalidatePredictions(rootStore, { appId: context.appId, versionId: context.versionId }))();

    /**
     * Add a prebuilt entities to the current app.
     *
     * @param param0.id The id of the prebuilt entity to delete.
     */
    const deletePrebuiltEntities = async ({ context, id, tracker }: HandlerParams<{ id: LuisId }>) => {
        if (tracker) {
            tracker.setContext(id);
        }

        const prebuiltEntityService = getPrebuiltEntityService({ context });
        const logTimedActivity = logger.createTimedActivityLogger('DeletePrebuiltEntities', () => ({ entityId: id }));
        await logTimedActivity(prebuiltEntityService.deleteEntity(id));

        rootStore.currentLuisAppStore.currentAppVersionStore.entityStoreHouse.removeById(id);
    };

    /**
     * Add a prebuilt domain entities to the current app.
     *
     * @param param0.prebuiltDomains The prebuilt domain entities to be added.
     */
    const addPrebuiltDomainEntities = async ({ prebuiltDomains, context }: HandlerParams<{ prebuiltDomains: DomainModel[] }>) =>
        flow(async () => {
            const prebuiltEntityService = getPrebuiltEntityService({ context });
            const logTimedActivity = logger.createTimedActivityLogger<string[]>('AddPrebuiltDomainEntities', () => ({
                prebuiltDomains: prebuiltDomains.map(name => name)
            }));

            const servicePromises = prebuiltDomains.map(domain => prebuiltEntityService.addPrebuiltDomainEntity(domain));
            await logTimedActivity(Promise.all(servicePromises));
            await loadEntitiesHelper(rootStore)({ context: { ...context, forceRefresh: true } });
        }, addDirtyState({ appId: context.appId, versionId: context.versionId, rootStore }))();

    return {
        loadPrebuiltEntities,
        addPrebuiltEntities,
        addPrebuiltDomainEntities,
        deletePrebuiltEntities
    };
};
